import { useQuery } from '@tanstack/react-query'
import PropTypes from 'prop-types'
import { lazy, Suspense, useState } from 'react'
import { useParams } from 'react-router'

import { Button } from '@/components/elements/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/elements/dialog'
import { Input } from '@/components/elements/input'
import { Label } from '@/components/elements/label'
import { getCertificates } from '@/services/certificates.service'
import { createStripeCheckout } from '@/services/credit_bank.service'
import { formatCurrency, toast } from '@/utils/helpers'

// Lazy load Stripe components
const StripeCheckout = lazy(() => import('./StripeCheckout'))

const PurchaseModal = ({ isOpen, onClose, onSuccess }) => {
  const { entityId } = useParams()
  const [amount, setAmount] = useState('')
  const [reference, setReference] = useState('')
  const [isCreatingCheckout, setIsCreatingCheckout] = useState(false)
  const [clientSecret, setClientSecret] = useState(null)
  const [error, setError] = useState(null)

  // Fetch certificates for pricing reference
  const { data: certificates } = useQuery({
    queryKey: ['certificates', entityId],
    queryFn: () =>
      getCertificates({
        entityId,
        filters: { cost__gt: 0 },
        pagination: { pageSize: 100, pageIndex: 0 },
        sorting: [{ id: 'name', desc: false }],
      }),
    enabled: !!entityId && isOpen,
  })

  const handleAmountChange = (e) => {
    const { value } = e.target
    // Only allow positive numbers with up to 2 decimal places
    if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
      setAmount(value)
      setError(null)
    }
  }

  const handleCreateCheckout = async () => {
    const numAmount = parseFloat(amount)

    if (!amount || numAmount <= 0) {
      setError('Please enter a valid amount')
      return
    }

    setIsCreatingCheckout(true)
    setError(null)

    try {
      const response = await createStripeCheckout(entityId, numAmount, reference)
      setClientSecret(response.clientSecret)
    } catch (err) {
      console.error('Error creating checkout:', err)
      setError(err.message || 'Failed to create checkout session')
      toast('Failed to create checkout session', 'error')
    } finally {
      setIsCreatingCheckout(false)
    }
  }

  const handlePaymentSuccess = () => {
    toast('Payment successful! Credits will be added to your account.', 'success')
    onSuccess()
    handleClose()
  }

  const handleClose = () => {
    setAmount('')
    setReference('')
    setClientSecret(null)
    setError(null)
    onClose()
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-[500px]" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Purchase Credits</DialogTitle>
          <DialogDescription>
            Enter the amount of credits you&apos;d like to purchase. Credits are added at a 1:1
            ratio ($1 = 1 credit).
            {/* Certificate Pricing Reference */}
            <div className="space-y-2 pt-4">
              <span>Course Pricing Reference</span>

              <div className="max-h-48 overflow-y-auto rounded-md border p-3">
                {certificates?.results && certificates.results.length > 0 ? (
                  <div className="space-y-1">
                    {certificates.results.map((cert) => (
                      <div key={cert.id} className="flex justify-between py-1 text-sm">
                        <span className="text-muted-foreground">{cert.name}</span>
                        <span className="font-medium">{formatCurrency(cert.cost)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm">No courses available</p>
                )}
              </div>
            </div>
          </DialogDescription>
        </DialogHeader>

        {!clientSecret ? (
          // Step 1: Amount input
          <div className="space-y-4 py-4">
            {/* Amount Input */}
            <div className="space-y-2">
              <Label htmlFor="amount">Amount (USD)</Label>
              <div className="relative">
                <span className="absolute top-1/2 left-3 -translate-y-1/2 text-gray-500">$</span>
                <Input
                  id="amount"
                  type="text"
                  inputMode="decimal"
                  placeholder="0.00"
                  value={amount}
                  onChange={handleAmountChange}
                  className="pl-7"
                  disabled={isCreatingCheckout}
                />
              </div>
              {error && <p className="text-sm text-red-500">{error}</p>}
              {amount && parseFloat(amount) > 0 && (
                <p className="text-sm text-gray-500">
                  You will receive {parseFloat(amount).toFixed(2)} credits
                </p>
              )}
            </div>

            {/* Reference Input */}
            <div className="space-y-2">
              <Label htmlFor="reference">Notes (Optional)</Label>
              <Input
                id="reference"
                type="text"
                placeholder="e.g., 5 EMT certs, Q1 training budget"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                maxLength={255}
                disabled={isCreatingCheckout}
              />
              <p className="text-muted-foreground text-xs">
                Add notes about this purchase for your records
              </p>
            </div>
          </div>
        ) : (
          // Step 2: Stripe payment form
          <div className="py-4">
            <Suspense fallback={<div className="py-8 text-center">Loading payment form...</div>}>
              <StripeCheckout
                clientSecret={clientSecret}
                onSuccess={handlePaymentSuccess}
                onCancel={handleClose}
              />
            </Suspense>
          </div>
        )}

        {!clientSecret && (
          <DialogFooter>
            <Button variant="outline" onClick={handleClose} disabled={isCreatingCheckout}>
              Cancel
            </Button>
            <Button onClick={handleCreateCheckout} disabled={!amount || isCreatingCheckout}>
              {isCreatingCheckout ? 'Processing...' : 'Continue to Payment'}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default PurchaseModal

PurchaseModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
}
